import { TaskBoardModel } from "@jugl-web/rest-api/tasks/models/TaskBoard";
import { Button, FormGroup, TextInput } from "@jugl-web/ui-kit";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, ReactNode, useCallback } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { FormSection } from "@jugl-web/ui-kit/forms/FormSection";
import { AppModule } from "../../../common/types";
import { UserSelect } from "../../../users/components/UserSelect/UserSelect";
import {
  BOARD_DESCRIPTION_MAX_LENGTH,
  BOARD_NAME_MAX_LENGTH,
  BOARD_NAME_MIN_LENGTH,
  BOARD_ID_PREFIX_MAX_LENGTH,
} from "../../consts";
import { boardIdPrefixValidator } from "../../validators";
import { ReactComponent as GeneralSectionIcon } from "./assets/general-section.svg";
import { BoardSettingsSection } from "./components/BoardSettingsSection";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { BoardColorPicker } from "./components/BoardColorPicker";
import { useBoardSettingsHints } from "./hooks/useBoardSettingsHints";
import { ManageBoardFormValues } from "./types";

type ManageBoardFormMode =
  | { type: "basic"; onTriggerAdvancedMode: () => void }
  | { type: "advanced" };

interface ManageBoardFormProps {
  entityId: string;
  meId: string;
  formId: string;
  form: UseFormReturn<ManageBoardFormValues>;
  mode: ManageBoardFormMode;
  editingConfig?: {
    board: TaskBoardModel;
    canManage: boolean;
  };
  className?: string;
  onSubmit: () => void;
}

const userFormFields = ["owners", "members", "viewers"] as const;

export const ManageBoardForm: FC<ManageBoardFormProps> = ({
  entityId,
  meId,
  formId,
  form,
  mode,
  editingConfig,
  className,
  onSubmit,
}) => {
  const { register, formState, control, getValues, setValue, watch } = form;

  const { isMobile } = useAppVariant();

  const boardSettingsHints = useBoardSettingsHints();
  const { t } = useTranslations();

  const boardName = watch("name");
  const boardColor = watch("color");

  const boardNamePlaceholder = t({
    id: "tasks-board-form.board-name.placeholder",
    defaultMessage: "Enter",
  });

  const isEditing = !!editingConfig;
  const isFormReadonly = isEditing && !editingConfig.canManage;

  const handleUsersChange = useCallback(
    (field: (typeof userFormFields)[number]) => (userIds: string[]) => {
      const fieldsToCheck = userFormFields.filter((f) => f !== field);

      fieldsToCheck.forEach((f) => {
        const currentUsers = getValues(f);
        const filteredUsers = currentUsers.filter(
          (id) => !userIds.includes(id)
        );
        setValue(f, filteredUsers, { shouldDirty: true });
      });

      setValue(field, userIds, { shouldDirty: true });
    },
    [getValues, setValue]
  );

  const generalSectionContent = (
    <div
      className={cx("flex w-full max-w-[1000px] flex-col items-center gap-8", {
        "gap-6": mode.type === "basic",
      })}
    >
      {isFormReadonly ? (
        <div className="flex flex-col items-center gap-8">
          <BoardColorPicker
            isReadonly
            name={boardName}
            namePlaceholder={boardNamePlaceholder}
            color={boardColor}
            size="lg"
          />
          <div className="flex flex-col items-center gap-3">
            <h3 className="m-0 font-secondary text-xl leading-[150%] font-medium text-dark">
              {editingConfig.board.name}
            </h3>
            <p className="m-0 font-secondary text-base leading-4 text-dark-700">
              {editingConfig.board.description}
            </p>
          </div>
        </div>
      ) : (
        <>
          <FormGroup
            label={t({
              id: "form-controls.icon-and-board-name.label",
              defaultMessage: "Icon & Board name",
            })}
            isRequired
            classNames={{ wrapperClassName: "w-full" }}
          >
            <div className="flex w-full items-start gap-4">
              <Controller
                name="color"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <BoardColorPicker
                    name={boardName}
                    namePlaceholder={boardNamePlaceholder}
                    color={value}
                    size="md"
                    isReadonly={isFormReadonly}
                    onColorChange={onChange}
                  />
                )}
              />
              <TextInput
                isRequired
                isInvalid={!!formState.errors.name}
                placeholder={boardNamePlaceholder}
                lengthIndicator={{ max: BOARD_NAME_MAX_LENGTH }}
                maxLength={BOARD_NAME_MAX_LENGTH}
                errorMessage={formState.errors.name?.message}
                {...register("name", {
                  required: {
                    value: true,
                    message: t(
                      {
                        id: "form-controls.board-name.min-length-error-message",
                        defaultMessage:
                          "Name should contain at least {count} characters",
                      },
                      { count: BOARD_NAME_MIN_LENGTH }
                    ),
                  },
                  maxLength: BOARD_NAME_MAX_LENGTH,
                })}
                classNames={{ wrapperClassName: "w-full" }}
              />
            </div>
          </FormGroup>
          <TextInput
            label={t({
              id: "form-controls.board-description.label",
              defaultMessage: "Description",
            })}
            placeholder={t({
              id: "form-controls.board-description.placeholder",
              defaultMessage: "Enter",
            })}
            lengthIndicator={{ max: BOARD_DESCRIPTION_MAX_LENGTH }}
            maxLength={BOARD_DESCRIPTION_MAX_LENGTH}
            {...register("description")}
            classNames={{ wrapperClassName: "w-full" }}
          />
          {mode.type === "advanced" && (
            <TextInput
              label={t({
                id: "form-controls.board-id-prefix.label",
                defaultMessage: "Board ID Prefix",
              })}
              placeholder={t({
                id: "form-controls.board-description.placeholder",
                defaultMessage: "Enter",
              })}
              tooltip={
                isMobile ? undefined : boardSettingsHints.boardIdPrefixHint
              }
              lengthIndicator={{ max: BOARD_ID_PREFIX_MAX_LENGTH }}
              maxLength={BOARD_ID_PREFIX_MAX_LENGTH}
              {...register("prefix", {
                maxLength: BOARD_ID_PREFIX_MAX_LENGTH,
                validate: (value) => {
                  if (value && value.trim().length === 0) {
                    return t({
                      id: "form-controls.board-id-prefix.no-spaces-error-message",
                      defaultMessage:
                        "Board ID Prefix cannot contain only spaces",
                    });
                  }
                  if (value && !boardIdPrefixValidator(value)) {
                    return t({
                      id: "form-controls.board-id-prefix.no-special-chars-error-message",
                      defaultMessage:
                        "Board ID Prefix can only contain letters and numbers",
                    });
                  }
                  return true;
                },
                setValueAs: (value) => value.trim(),
              })}
              classNames={{ wrapperClassName: "w-full" }}
              isInvalid={!!formState.errors.prefix}
              errorMessage={formState.errors.prefix?.message}
            />
          )}
        </>
      )}
      <div
        className={cx("flex w-full flex-col gap-6", {
          "flex md:flex-row": mode.type === "advanced",
        })}
      >
        <Controller
          control={control}
          name="owners"
          render={({ field }) => (
            <UserSelect
              entityId={entityId}
              meId={meId}
              label={t({
                id: "form-controls.board-admins.label",
                defaultMessage: "Admins",
              })}
              classNames={{ wrapperClassName: "w-full" }}
              tooltip={
                !isMobile && (
                  <TooltipList items={boardSettingsHints.ownerRoleHints} />
                )
              }
              userIds={field.value}
              isReadonly={isFormReadonly}
              module={AppModule.tasks}
              shouldExcludeMe
              dialogTitle={t({
                id: "tasks-page.select-board-admins",
                defaultMessage: "Select board admins",
              })}
              inputLabel={
                field.value.length > 0
                  ? t(
                      {
                        id: "tasks-page.board-admins-count",
                        defaultMessage:
                          "{count} {count, plural, one {admin} other {admins}}",
                      },
                      { count: field.value.length }
                    )
                  : undefined
              }
              emptyStateLabel={
                isFormReadonly
                  ? t({
                      id: "tasks-page.no-admins",
                      defaultMessage: "No admins",
                    })
                  : undefined
              }
              onUsersChange={handleUsersChange("owners")}
            />
          )}
        />
        <Controller
          control={control}
          name="members"
          render={({ field }) => (
            <UserSelect
              entityId={entityId}
              meId={meId}
              label={t({
                id: "form-controls.board-members.label",
                defaultMessage: "Members",
              })}
              classNames={{ wrapperClassName: "w-full" }}
              tooltip={
                !isMobile && (
                  <TooltipList items={boardSettingsHints.memberRoleHints} />
                )
              }
              userIds={field.value}
              isReadonly={isFormReadonly}
              module={AppModule.tasks}
              shouldExcludeMe
              dialogTitle={t({
                id: "tasks-page.select-board-members",
                defaultMessage: "Select board members",
              })}
              inputLabel={
                field.value.length > 0
                  ? t(
                      {
                        id: "tasks-page.board-members-count",
                        defaultMessage:
                          "{count} {count, plural, one {member} other {members}}",
                      },
                      { count: field.value.length }
                    )
                  : undefined
              }
              emptyStateLabel={
                isFormReadonly
                  ? t({
                      id: "tasks-page.no-members",
                      defaultMessage: "No members",
                    })
                  : undefined
              }
              onUsersChange={handleUsersChange("members")}
            />
          )}
        />
        <Controller
          control={control}
          name="viewers"
          render={({ field }) => (
            <UserSelect
              entityId={entityId}
              meId={meId}
              label={t({
                id: "form-controls.board-viewers.label",
                defaultMessage: "Viewers",
              })}
              classNames={{ wrapperClassName: "w-full" }}
              tooltip={
                !isMobile && (
                  <TooltipList items={boardSettingsHints.viewerRoleHints} />
                )
              }
              userIds={field.value}
              isReadonly={isFormReadonly}
              module={AppModule.tasks}
              dialogTitle={t({
                id: "tasks-page.select-board-viewers",
                defaultMessage: "Select board viewers",
              })}
              shouldExcludeMe
              inputLabel={
                field.value.length > 0
                  ? t(
                      {
                        id: "tasks-page.board-viewers-count",
                        defaultMessage:
                          "{count} {count, plural, one {viewer} other {viewers}}",
                      },
                      { count: field.value.length }
                    )
                  : undefined
              }
              emptyStateLabel={
                isFormReadonly
                  ? t({
                      id: "tasks-page.no-viewers",
                      defaultMessage: "No viewers",
                    })
                  : undefined
              }
              onUsersChange={handleUsersChange("viewers")}
            />
          )}
        />
        {mode.type === "basic" && (
          <Button
            variant="tertiary"
            type="button"
            onClick={mode.onTriggerAdvancedMode}
            iconEnd={<PlusIcon />}
            className="h-10 self-start font-secondary font-normal text-dark-800"
          >
            {t({
              id: "tasks-page.more-board-details",
              defaultMessage: "More Board Details",
            })}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <form
      id={formId}
      className={cx(
        "flex w-full max-w-[1000px] flex-col items-center gap-8",
        className
      )}
      onSubmit={onSubmit}
    >
      {mode.type === "basic" ? (
        generalSectionContent
      ) : (
        <FormSection
          title={t({
            id: "tasks-board-form.section.general-info.title",
            defaultMessage: "General Info",
          })}
          icon={<GeneralSectionIcon />}
        >
          {generalSectionContent}
        </FormSection>
      )}
      {mode.type === "advanced" && (
        <BoardSettingsSection form={form} isFormReadonly={isFormReadonly} />
      )}
    </form>
  );
};

const TooltipList = ({ items }: { items: ReactNode[] }) => (
  <ul className="m-0 flex flex-col gap-2.5 pl-4">
    {items.map((item, index) => (
      <li key={+index}>{item}</li>
    ))}
  </ul>
);
